import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import styles from './NewsDetailed.module.scss';
import { PaginationRoutes } from '@/components/PaginationRoutes';
import { SettingsType, Variants } from '@/types';
import { fetchData } from '@/services/fetchData';
import { Layout } from '@/layout';
import { BACKEND_URL } from '@/constants';
import Link from 'next/link';
import { Gallery } from '@/components/Gallery';
import { Button } from '@/atoms/Button';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';

const NewsDetailed = ({
  content,
  menu,
  translations,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const itemData = content.item;
  const backendHTML = { __html: itemData.text };
  const mobileTable = itemData.pictureTable;
  const { isMobile } = useAdaptiveWidth();
  const showGallery =
    itemData.gallery.length > 0 || itemData.galleryMob.length > 0;

  return (
    <Layout data={menu} meta={content.meta}>
      <main className={styles.page}>
        <PaginationRoutes
          routes={[
            { link: '/news', text: translations.linkNews },
            {
              link: `/news/${itemData.code}`,
              text: itemData.name,
            },
          ]}
        />
        {showGallery && (
          <Gallery
            galleryMob={itemData.galleryMob}
            gallery={itemData.gallery}
          />
        )}
        {content.item.name && (
          <h1 className={styles.title}>{content.item.name}</h1>
        )}
        <div
          className={`info ${mobileTable && isMobile ? 'hiddenTable' : ''}`}
          dangerouslySetInnerHTML={backendHTML}
        />
        {mobileTable && isMobile && (
          <Link
            href={`${BACKEND_URL}${mobileTable}`}
            target="_blank"
            className={styles.mobileTable}
          >
            <img
              loading="lazy"
              src={`${BACKEND_URL}${mobileTable}`}
              alt="table"
            />
            <Button
              variant={Variants.Outline}
              text={translations.buttonMore}
              onClick={() =>
                window && window.open(`${BACKEND_URL}${mobileTable}`, '_blank')
              }
              className={styles.mobileTableButton}
            />
          </Link>
        )}
      </main>
    </Layout>
  );
};

export async function getServerSideProps({
  params,
  locale,
}: GetServerSidePropsContext) {
  const paramsId = params?.news;

  const content = await fetchData({
    url: `/content/news/${paramsId}/view`,
    locale,
  });

  const menu: SettingsType = await fetchData({
    url: `/settings/view`,
    locale,
  });

  const isError = !content || content.code !== 200;
  if (isError) return { notFound: true };

  return {
    props: {
      content,
      menu,
      translations: menu.translations['/news/[news]'],
    },
  };
}

export default NewsDetailed;
